import type { RunStep } from '@app/shared';

/**
 * Pure, render-independent summary of a transcript step (#148). Drives the collapsed
 * `AccordionSummary` header of each step in `RunTranscript` so a folded transcript is
 * still scannable: the turn type, the tool(s) involved, and a one-line gist.
 *
 * Kept free of React/MUI so it can be unit-tested in the node-env web vitest runner
 * (`src/**\/*.test.ts`). Back-compat with older runs: every enriched field
 * (`text`, `thinking`, `toolUses`, tokens) is optional — a bare payload still yields a
 * sensible descriptor.
 */

export interface StepSummary {
  /** Human turn-type label, e.g. "Model turn". */
  title: string;
  /** Optional monospace tool label for the header: a tool name, or "calls: a, b". */
  toolLabel?: string;
  /** One-line gist, safe to render truncated (`noWrap`). May be empty. */
  summary: string;
}

export const KIND_LABEL: Record<RunStep['kind'], string> = {
  model_turn: 'Model turn',
  tool_call: 'Tool call',
  approval: 'Approval',
  wake: 'Wake',
  system: 'System',
};

interface ToolUse {
  name: string;
  input?: unknown;
}

function isToolUse(v: unknown): v is ToolUse {
  return typeof v === 'object' && v !== null && typeof (v as { name?: unknown }).name === 'string';
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
}

/** First non-empty, trimmed line of a (possibly multi-line) string. */
function firstLine(text: string): string {
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line.length > 0) return line;
  }
  return '';
}

function toolNames(payload: { toolUses?: unknown }): string[] {
  return Array.isArray(payload.toolUses)
    ? payload.toolUses.filter(isToolUse).map((tu) => tu.name)
    : [];
}

function summarizeModelTurn(step: RunStep): StepSummary {
  const payload = step.payload as {
    text?: unknown;
    thinking?: unknown;
    toolUses?: unknown;
  };
  const text = str(payload.text);
  const thinking = str(payload.thinking);
  const names = toolNames(payload);

  const toolLabel = names.length > 0 ? `calls: ${names.join(', ')}` : undefined;

  let summary = '';
  if (text) {
    summary = firstLine(text);
  } else if (names.length > 0) {
    summary = `decided to call ${names.join(', ')}`;
  } else if (thinking) {
    summary = '(reasoning)';
  }

  return { title: KIND_LABEL.model_turn, toolLabel, summary };
}

/** Tool-step statuses that mean the call did not succeed (§ `toolStepStatus`). */
const FAILED_STATUSES = new Set(['failed', 'denied']);

function summarizeToolCall(step: RunStep): StepSummary {
  const tool = str(step.tool) ?? undefined;
  const status = str(step.toolStatus) ?? undefined;

  // Prefer an explicit result/error hint from the payload; fall back to the status.
  const payload = step.payload as { error?: unknown; result?: unknown; summary?: unknown };
  const errorHint = str((payload.error as { message?: unknown })?.message) ?? str(payload.error);
  const resultHint = str(payload.summary) ?? str(payload.result);

  let summary: string;
  if (status !== undefined && FAILED_STATUSES.has(status)) {
    summary = errorHint ? `${status}: ${firstLine(errorHint)}` : status;
  } else if (resultHint) {
    summary = firstLine(resultHint);
  } else if (status === 'ok') {
    summary = tool ? `${tool} ok` : 'ok';
  } else {
    summary = status ?? tool ?? '';
  }

  return { title: KIND_LABEL.tool_call, toolLabel: tool, summary };
}

function summarizeGeneric(step: RunStep): StepSummary {
  const payload = step.payload as {
    text?: unknown;
    reason?: unknown;
    wake?: unknown;
    kind?: unknown;
    note?: unknown;
  };
  const summary = firstLine(
    str(payload.text) ??
      str(payload.reason) ??
      str(payload.note) ??
      str(payload.wake) ??
      str(payload.kind) ??
      '',
  );
  return { title: KIND_LABEL[step.kind], summary };
}

/**
 * Summarize one transcript step for its collapsed header. Never throws on malformed
 * payloads — unknown/empty shapes degrade to just the kind label.
 */
export function summarizeStep(step: RunStep): StepSummary {
  switch (step.kind) {
    case 'model_turn':
      return summarizeModelTurn(step);
    case 'tool_call':
      return summarizeToolCall(step);
    default:
      return summarizeGeneric(step);
  }
}
